/*
 * 文件白名单 + 校验
 *
 * 两套规则：
 *   - OFFICE_*  ：首页上传"主文档"用，必须是 Word/Excel（之后才能进文档预览）
 *   - COMPOSER_*：会话输入框附件用，范围更宽（图片 / Office / PDF / CSV…），
 *                 因为这些只是给 AI 的辅助上下文，不需要进预览
 */

// ─── 首页主文档 ────────────────────────────────────────
// AI 资料员目前**只支持 Word / Excel**, PPT 暂不支持 (LLM 工具链没接 pptx
// 编辑). 浏览器 file picker 会按 accept 自动隐藏 .ppt/.pptx 文件,
// 拖拽 / 粘贴 PPT 时 validateOfficeFile 兜底报错文案.
// composer 附件 (chat 输入框) 仍允许 PPT 作参考资料, 见下方 COMPOSER_*.
export const OFFICE_ACCEPT_TYPES = '.docx,.doc,.xlsx,.xls'

export const OFFICE_ALLOWED_EXTS = ['docx', 'doc', 'xlsx', 'xls'] as const

export const OFFICE_ALLOWED_MIMES = new Set<string>([
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
])

export function getFileExt(name: string): string {
  return (name.split('.').pop() || '').toLowerCase()
}

/** 返回 null = 合法；否则返回给用户看的错误文案 */
export function validateOfficeFile(file: File): string | null {
  const ext = getFileExt(file.name)
  if ((OFFICE_ALLOWED_EXTS as readonly string[]).includes(ext)) return null
  if (file.type && OFFICE_ALLOWED_MIMES.has(file.type)) return null
  return `只支持 Word / Excel 文件（.docx / .doc / .xlsx / .xls），当前文件「${file.name}」不支持`
}

// ─── 会话 composer 附件 ───────────────────────────────
// 比主文档宽松：图片用于"看图填表"，PDF/CSV/TXT 用于参考资料
export const COMPOSER_ACCEPT_TYPES = [
  '.docx,.doc,.xlsx,.xls,.pptx,.ppt,.csv,.pdf,.txt,.md',
  'image/*'
].join(',')

export const COMPOSER_ALLOWED_EXTS = [
  'docx',
  'doc',
  'xlsx',
  'xls',
  'pptx',
  'ppt',
  'csv',
  'pdf',
  'txt',
  'md',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'heic',
  'heif',
  'svg'
] as const

const COMPOSER_EXTRA_MIMES = new Set<string>([
  'application/pdf',
  'text/csv',
  'text/plain',
  'text/markdown'
])

export function validateComposerFile(file: File): string | null {
  const ext = getFileExt(file.name)
  if ((COMPOSER_ALLOWED_EXTS as readonly string[]).includes(ext)) return null
  if (file.type) {
    if (file.type.startsWith('image/')) return null
    if (OFFICE_ALLOWED_MIMES.has(file.type)) return null
    if (COMPOSER_EXTRA_MIMES.has(file.type)) return null
  }
  return `不支持的文件类型：「${file.name}」。仅支持图片 / Word / Excel / PDF / CSV / 文本`
}

// ─── 文件类目（用于 chip 图标颜色 / 类型路由） ─────────
export type FileKind =
  | 'image'
  | 'spreadsheet'
  | 'word'
  | 'presentation'
  | 'pdf'
  | 'text'
  | 'other'

const SPREADSHEET_EXTS = new Set(['xlsx', 'xls', 'csv'])
const WORD_EXTS = new Set(['docx', 'doc'])
const PRESENTATION_EXTS = new Set(['pptx', 'ppt'])
const IMAGE_EXTS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'heic',
  'heif',
  'svg'
])
const TEXT_EXTS = new Set(['txt', 'md'])

export function classifyFile(ext: string, mime?: string): FileKind {
  const e = ext.toLowerCase()
  if (IMAGE_EXTS.has(e) || (mime && mime.startsWith('image/'))) return 'image'
  if (SPREADSHEET_EXTS.has(e)) return 'spreadsheet'
  if (WORD_EXTS.has(e)) return 'word'
  if (PRESENTATION_EXTS.has(e)) return 'presentation'
  if (e === 'pdf') return 'pdf'
  if (TEXT_EXTS.has(e)) return 'text'
  return 'other'
}

/** 根据扩展名 / 浏览器报的 type 给出标准 MIME，发送给 /api/chat 的 file part 使用 */
const EXT_TO_MIME: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  heic: 'image/heic',
  heif: 'image/heif',
  svg: 'image/svg+xml',
  pdf: 'application/pdf',
  txt: 'text/plain',
  md: 'text/markdown',
  csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
}

export function resolveMediaType(name: string, fallback?: string): string {
  const ext = getFileExt(name)
  if (EXT_TO_MIME[ext]) return EXT_TO_MIME[ext]
  if (fallback) return fallback
  return 'application/octet-stream'
}
